import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// convertMaturityToYears calls dayjs().utc(); register the plugin here rather than relying
// on some other module having been imported first
dayjs.extend(utc);

/**
 * Converts maturity date string to integer years from current date
 * @param {string} maturityValue - Maturity value (could be date string or already in year format with 'Y' suffix)
 * @returns {number|null} - Integer years from current date or null if invalid
 */
export const convertMaturityToYears = (maturityValue) => {
  if (!maturityValue) return null;
  
  const strValue = maturityValue.toString();
  
  // If it already includes 'Y', it's already in year format
  if (strValue.includes('Y')) {
    const parsedValue = parseFloat(strValue.replace('Y', ''));
    // Return any float value as-is (no restriction / no integer rounding)
    return parsedValue;
  }
  
  // Try to parse as date string. Parse as UTC, not local: dayjs("2027-03-10") is local
  // midnight, and differencing that against a UTC "now" shifts the result by the timezone
  // offset (-5:30 in IST), which is enough to lose a whole day.
  const maturityDate = dayjs.utc(strValue);
  //if format is like this '2025-11-03' then only then it will be valid
  if (maturityDate.isValid() && maturityDate.format("YYYY-MM-DD") === strValue) {

    // startOf('day') matters: diff(...,'day') truncates the partial day, so comparing
    // against a "now" that carries a time component silently loses one day.
    const today = dayjs().utc().startOf('day');
    const diffInDays = maturityDate.diff(today, 'day');
    // Convert days to years as float. 4dp because 0.01y is 3.65 days — enough to shift the
    // reconstructed date by up to 2 days; 0.0001y is under an hour, so the tenor always
    // resolves back to the exact calendar day. Number() drops trailing zeros, so whole
    // years stay clean (2.0000 -> 2).
    return Number((diffInDays / 365).toFixed(4));
  }
  
  // If it's just a number (already in years format)
  if (!isNaN(strValue)) {
    const parsedValue = parseFloat(strValue);
    // Return any float value as-is (no restriction / no integer rounding)
    return parsedValue;
  }
  
  return null;
};

/**
 * Days the maturity date preview pads a tenor by on each side
 * (see calculateDatePreview in BondCharacteristicsFields).
 */
export const MATURITY_TENOR_PADDING_DAYS = 90;

/**
 * Converts an absolute maturity bound to a tenor in years, inverting the padding the
 * preview will re-apply.
 *
 * Bounds sent by the backend are already widened to the rung's tolerance window, so the
 * preview's own -90/+90 would double-count it. Measuring the min tenor to (from + 90d) and
 * the max tenor to (to - 90d) makes the preview reconstruct the original dates exactly.
 * This inverts the frontend's own padding, so it stays exact regardless of the tolerance
 * the backend applied.
 *
 * @param {string} value - absolute date (YYYY-MM-DD)
 * @param {number} paddingDays - +90 for the lower bound, -90 for the upper bound
 * @returns {number|null} tenor in years, or null when value is not a date
 */
export const maturityDateToTenorYears = (value, paddingDays = 0) => {
  const years = convertMaturityToYears(value);
  if (years === null || Number.isNaN(years)) return null;
  return Number((years + paddingDays / 365).toFixed(4));
};

// IMP NOTE
// IMP NOTE
// IMP NOTE
// this is for view inventory only not related to search inventory
// Utility function to apply filters and navigate 
export const applyInventoryFilters = (filters, navigate, label) => {
  const filterInput = {};

  // Basic string filters
  const stringFields = [
    'type', 'subType', 'side', 'state', 'paymentFrequency', 
    'sector', 'incomeSource', 'minRatingMoody', 'maxRatingMoody',
    'minRatingSP', 'maxRatingSP', 'issuer', 'underlyingRating', 'minimumRating',
    'minimumRatingMoody', 'securityCharge'
  ];
  
  stringFields.forEach(field => {
    if (filters[field]) {
      filterInput[field] = filters[field];
    }
  });

  // Date fields
  // Pass maturity bounds as-is (do not parse) — the value may be a "5Y" tenor or an
  // absolute date, and the backend handles both. Accept either spelling: saved
  // characteristics carry maturityMin/maturityMax, older ones minMaturity/maxMaturity.
  if (filters.maturityMin || filters.minMaturity) {
    filterInput.minMaturity = filters.maturityMin || filters.minMaturity;
  }
  if (filters.maturityMax || filters.maxMaturity) {
    filterInput.maxMaturity = filters.maturityMax || filters.maxMaturity;
  }
  if (filters.nextCallDate) {
    filterInput.nextCallDate = dayjs(filters.nextCallDate).format("YYYY-MM-DD");
  }

  // Numeric fields (ensuring they're not empty strings or undefined)
  const numericFields = [
    'minCoupon', 'maxCoupon',
    'minQty', 'maxQty',
    'minYtw', 'maxYtw',
    'minPrice', 'maxPrice',
    'minYtm', 'maxYtm',
  ];

  numericFields.forEach(field => {
    if (filters[field] !== undefined && filters[field] !== '' && !isNaN(filters[field])) {
      filterInput[field] = Number(filters[field]);
    }
  });

  // Handle duration based on comparison operator
  if (filters.duration) {
   try{
     const durationValue = filters.duration.replace(/[<>=]/g, '');
    if (filters.duration.startsWith('>=')) {
      filterInput.minDuration = Number(durationValue);
    } else if (filters.duration.startsWith('<=')) {
      filterInput.maxDuration = Number(durationValue);
    }
   }catch(e){
      console.error("Error parsing duration:", e);
    }
  }

  // Boolean fields converted to Byte (0/1)
  const booleanFields = ['isActive', 'insured', 'altMinTax', 'taxable', 'zeroCoupon'];
  
  booleanFields.forEach(field => {
    if (filters[field] !== undefined && filters[field] !== '') {
      filterInput[field] = filters[field] === 'Yes' || filters[field] === true ? 1 : 0;
    }
  });

  // Special handling for callable/call field (they represent the same thing)
  if (filters.callable) {
    filterInput.callable = filters.callable === 'Yes' ? 'Yes' : 'No';
  } else if (filters.call) {
    filterInput.callable = filters.call === 'Yes' ? 'Yes' : 'No';
  }

  // Rating handling for Corp and Muni types
  if ((filters.type === 'Corp' || filters.type === 'Muni') && filters.minimumRating) {
    filterInput.minRatingSP = filters.minimumRating;
    filterInput.maxRatingSP = 'AAA';
    // Optional: Add Moody's ratings if needed
    // filterInput.minRatingMoody = equivalent Moody's rating
    // filterInput.maxRatingMoody = 'Aaa'
  }

  // Moody's rating mapping
  if (filters.minimumRatingMoody) {
    filterInput.minRatingMoody = filters.minimumRatingMoody;
    filterInput.maxRatingMoody = 'Aaa';
  }

  // Compute source according to rules:
  // 1) If filters.source exists (non-null/non-empty) keep it.
  // 2) Else if label === 'yieldtable' -> 'yieldtable'
  // 3) Else -> 'filteredSource'
  let computedSource;
  if (filters && filters.source !== undefined && filters.source !== null && filters.source !== '') {
    computedSource = filters.source;
  } else if (label === 'yieldtable') {
    computedSource = 'yieldtable';
  } else {
    computedSource = 'filteredSource';
  }

  const statePayload = {
    ...filterInput,
    source: computedSource,
    // __navTs: Date.now(),
  };

  if (typeof navigate !== "function") {
    return statePayload;
  }

  if (label === "bondladder") {
    navigate("/bond-ladder-detail", {
      state: statePayload,
    });
    return statePayload;
  }
  navigate("/inventory", {
    state: statePayload,
  });
  return statePayload;
};

// // Hook version for components (optional, for FilterPopup)
// export const useInventoryFilters = () => {
//   const navigate = useNavigate();
//   return (filters) => applyInventoryFilters(filters, navigate);
// };
